/**
 * DemoAccessGuard — V22.9.9
 *
 * Gates the entire demo tenant experience. Any user landing on a protected
 * route who:
 *   * is unauthenticated, OR
 *   * is authenticated to the Meridian Demo Sandbox tenant but the JWT
 *     either lacks the `purpose:demo_access` claim or the demo_access_
 *     expires_at on their user record has passed,
 * is bounced to "/" with an informational modal "Demo Access Required".
 *
 * Approved demo users (role=demo_observer, valid 7-day window) pass through.
 * All non-demo tenants pass through unaffected.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { ShieldCheck } from "lucide-react";

const DEMO_TENANT_ORG_ID = "a93651c9-6cdc-4381-be7b-9a69548db02b";

export default function DemoAccessGuard({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (loading) return;
    // Only enforce when the user is logged into the demo tenant
    if (!isAuthenticated || !user) return;
    if (user.org_id !== DEMO_TENANT_ORG_ID) return;

    // Non-demo roles inside the demo tenant (admin/bcm_manager for upkeep) pass through
    if (user.role !== "demo_observer") return;

    // Approved demo_observer — check expiry
    const expires = user.demo_access_expires_at;
    if (expires) {
      const ts = new Date(expires).getTime();
      if (!Number.isNaN(ts) && ts < Date.now()) {
        setShowModal(true);
      }
    }
  }, [user, isAuthenticated, loading]);

  if (showModal) {
    return (
      <Dialog open onOpenChange={() => {}}>
        <DialogContent className="max-w-md" data-testid="demo-access-required-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-violet-600" />
              Demo Access Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>Your 7-day demo window has ended. To continue evaluating ResiliencyHUB,
            please request a new demo or contact our team.</p>
            <p className="text-xs text-slate-400">
              Multi-user enterprise plans get unlimited access.
            </p>
          </div>
          <div className="flex gap-2 mt-2">
            <Button
              onClick={() => navigate("/request-demo")}
              className="flex-1 bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="demo-access-required-cta"
            >
              Request New Demo
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              data-testid="demo-access-required-home"
            >
              Back to Home
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  return children;
}
